import { surveySetManagementService } from '@/services/surveySetManagementService/surveySetManagementService';
import { RootState } from '@/store';
import {
  CreateSurveySetDto,
  CreateSurveySetResponseDto,
  GetAllSurveySetsResponseDto,
  GetSurveySetByIdResponseDto,
  GetSurveySetsStatsResponseDto,
  SurveySetBySitesResponse,
  SurveySetDetails,
  SurveySetFilters,
  SurveySetListItem,
  SurveySetsStats,
  UpdateSurveySetBody,
  UpdateSurveySetResponseDto,
} from '@/types/surveySet.types';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

interface SurveySetState {
  list: {
    data: SurveySetListItem[];
    total?: number;
    page?: number;
    limit?: number;
    totalPages?: number;
  };
  selected: SurveySetDetails | null;
  stats: SurveySetsStats | null;
  loading: boolean;
  error: string | null;
  loadingStats: boolean;
  surveysetsBySite: SurveySetBySitesResponse;
  estimatedCount: number | null;
  loadingEstimatedCount: boolean;
  estimatedCountError: string | null;
  posterCodeData: GetPosterCodeResponseData['data'] | null;
  loadingPosterCode: boolean;
  posterCodeError: string | null;
  page: number;
  pageSize: number;
  filters: {
    search?: string;
    status?: boolean;
  };
}
export interface GetPosterCodeResponseData {
  success: boolean;
  message: string;
  data: {
    data: {
      site: any;
      id: any;
      title: any;
      posterCode(posterCode: any): unknown;
      data: object;
    };
    id: number;
    title: string;
    numberOfSurveys: number;
    posterCode: number;
    site: {
      id: number;
      name: string;
      project: {
        id: number;
        name: string;
        descriptions: string;
        primaryColour: string;
        secondaryColour: string;
        emoji: string;
        status: boolean;
        isDeleted: boolean;
        createdAt: string;
        createdBy: string;
        updatedAt: string;
        updatedBy: string;
        deletedAt: string | null;
        deletedBy: string | null;
        managers: Array<{
          id: number;
          projectId: number;
          blueNumberId: string;
          roleId: number;
          message: string;
          phoneNumber: string;
          countryCode: string;
          assignedAt: string;
        }>;
        sites: Array<{
          id: number;
          name: string;
          currency: string;
          geofence: {
            lat: number;
            lng: number;
          };
        }>;
      };
    };
  };
}

const initialState: SurveySetState = {
  list: {
    data: [],
  },
  selected: null,
  stats: null,
  loadingStats: false,
  loading: false,
  error: null,
  surveysetsBySite: {
    data: [],
  },
  estimatedCount: null,
  loadingEstimatedCount: false,
  estimatedCountError: null,
  posterCodeData: null,
  loadingPosterCode: false,
  posterCodeError: null,
  page: 1,
  pageSize: 10,
  filters: {
    search: '',
    status: undefined,
  },
};

export const getSurveySetsStats = createAsyncThunk<
  GetSurveySetsStatsResponseDto,
  void,
  {
    state: RootState;
    rejectValue: string;
  }
>('survey-sets/stats', async (_, { rejectWithValue }) => {
  try {
    return await surveySetManagementService.getSurveySetsStats();
  } catch (error: any) {
    const apiMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'Error fetching survey sets stats';
    return rejectWithValue(apiMessage);
  }
});

export const createSurveySet = createAsyncThunk<
  CreateSurveySetResponseDto,
  CreateSurveySetDto,
  {
    state: RootState;
    rejectValue: string;
  }
>('survey-sets/create', async (payload, { rejectWithValue }) => {
  try {
    return await surveySetManagementService.createSurveySet(payload);
  } catch (error: any) {
    const apiMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'Error creating survey set';
    return rejectWithValue(apiMessage);
  }
});

export const getAllSurveySets = createAsyncThunk<
  GetAllSurveySetsResponseDto,
  SurveySetFilters,
  {
    state: RootState;
    rejectValue: string;
  }
>('survey-sets/getAll', async (filters, { rejectWithValue }) => {
  try {
    return await surveySetManagementService.getAllSurveySets(filters);
  } catch (error: any) {
    const apiMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'Error fetching survey sets';
    return rejectWithValue(apiMessage);
  }
});

export const getSurveySetById = createAsyncThunk<
  GetSurveySetByIdResponseDto['data'],
  string,
  { rejectValue: string }
>('survey-sets/getById', async (id, { rejectWithValue }) => {
  try {
    const res = await surveySetManagementService.getSurveySetById(id);
    return res.data;
  } catch (error: any) {
    const apiMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'Error fetching Survey Set';
    return rejectWithValue(apiMessage);
  }
});

export const getSurveySetBySiteId = createAsyncThunk<
  SurveySetBySitesResponse,
  number,
  { rejectValue: string }
>('survey-setsgetBy/siteId', async (id, { rejectWithValue }) => {
  try {
    const res = await surveySetManagementService.getSurveySetBySiteId(id);
    return res;
  } catch (error: any) {
    const apiMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'Error fetching Survey Set';
    return rejectWithValue(apiMessage);
  }
});

export const updateSurveySet = createAsyncThunk<
  UpdateSurveySetResponseDto['data'],
  { id: string; data: UpdateSurveySetBody },
  { rejectValue: string }
>('survey-sets/update', async ({ id, data }, { rejectWithValue }) => {
  try {
    const res = await surveySetManagementService.updateSurveySet(id, data);
    return res.data;
  } catch (error: any) {
    const apiMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'Error updating Survey Set';
    return rejectWithValue(apiMessage);
  }
});

export const updateSurveySetStatus = createAsyncThunk<
  UpdateSurveySetResponseDto['data'],
  { id: string; data: UpdateSurveySetBody },
  { rejectValue: string }
>('survey-sets/updateStatus', async ({ id, data }, { rejectWithValue }) => {
  try {
    const res = await surveySetManagementService.updateSurveySetStatus(id, data);
    return res.data;
  } catch (error: any) {
    const apiMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'Error updating Survey Set Status';
    return rejectWithValue(apiMessage);
  }
});

export const getSurveySetUsersCount = createAsyncThunk<
  { data: { count: number } },
  { projectId?: number; siteId?: number; surveySetId?: number; posterCode?: string } | void,
  { rejectValue: string }
>('surveyset/getUsersCount', async (filters, { rejectWithValue }) => {
  try {
    return await surveySetManagementService.getSurveySetUsersCount(filters as any);
  } catch (error: any) {
    const apiMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'Error fetching audience count';
    return rejectWithValue(apiMessage);
  }
});

export const deleteSurveySet = createAsyncThunk<
  CreateSurveySetResponseDto,
  { id: string; data: number },
  { rejectValue: string }
>('survey-sets/delete', async ({ id, data }, { rejectWithValue }) => {
  try {
    return await surveySetManagementService.deleteSurveySet(id, data);
  } catch (error: any) {
    const apiMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'Error deleting survey set';
    return rejectWithValue(apiMessage);
  }
});

export const updateSurveyStatusInSet = createAsyncThunk<
  void,
  { surveySetId: string | number; surveyId: string | number; status: boolean },
  { rejectValue: string }
>(
  'survey-sets/updateSurveyStatusInSet',
  async ({ surveySetId, surveyId, status }, { rejectWithValue }) => {
    try {
      await surveySetManagementService.updateSurveyStatusInSet(surveySetId, surveyId, status);
      return;
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Error updating survey status';
      return rejectWithValue(apiMessage);
    }
  },
);

export const deleteSurveyFromSet = createAsyncThunk<
  void,
  { surveySetId: string | number; surveyId: string | number },
  { rejectValue: string }
>('survey-sets/deleteSurveyFromSet', async ({ surveySetId, surveyId }, { rejectWithValue }) => {
  try {
    await surveySetManagementService.deleteSurveyFromSet(surveySetId, surveyId);
    return;
  } catch (error: any) {
    const apiMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'Error removing survey from survey set';
    return rejectWithValue(apiMessage);
  }
});

export const getPosterCode = createAsyncThunk<
  GetPosterCodeResponseData['data'],
  string | number,
  { rejectValue: string }
>('surveyset/by-poster-code', async (posterCode, { rejectWithValue }) => {
  try {
    const response = await surveySetManagementService.getPosterCode(posterCode);
    return response.data;
  } catch (error: any) {
    const apiMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'Error fetching poster code data';
    return rejectWithValue(apiMessage);
  }
});

const surveySetSlice = createSlice({
  name: 'surveySet',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = action.payload;
    },

    setPage(state, action: PayloadAction<number>) {
      state.page = action.payload;
    },
    setPageSize(state, action: PayloadAction<number>) {
      state.pageSize = action.payload;
      state.page = 1; // reset page
    },

    resetPosterCodeState: (state) => {
      state.posterCodeData = null;
      state.loadingPosterCode = false;
      state.posterCodeError = null;
    },
    clearSurveySetsError(state) {
      state.error = null;
    },
    clearSelectedSurveySet(state) {
      state.selected = null;
    },
    updateSurveySetInList(
      state,
      action: PayloadAction<{ id: number; updates: Partial<SurveySetListItem> }>,
    ) {
      const index = state.list.data.findIndex((item) => item.id === action.payload.id);
      if (index !== -1) {
        state.list.data[index] = {
          ...state.list.data[index],
          ...action.payload.updates,
        };
      }
    },
    removeSurveySetFromList(state, action: PayloadAction<number>) {
      state.list.data = state.list.data.filter((item) => item.id !== action.payload);
      if (state.list.total) state.list.total -= 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSurveySetsStats.pending, (state) => {
        state.loadingStats = true;
      })
      .addCase(getSurveySetsStats.fulfilled, (state, action) => {
        state.loadingStats = false;
        state.stats = action.payload.data;
      })
      .addCase(getSurveySetsStats.rejected, (state, action) => {
        state.loadingStats = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(createSurveySet.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSurveySet.fulfilled, (state, action) => {
        state.loading = false;
        // state.list.data.unshift(action.payload.data);
      })
      .addCase(createSurveySet.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getAllSurveySets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllSurveySets.fulfilled, (state, action) => {
        state.loading = false;

        state.list.data = action.payload.data;
        state.list.total = action.payload.meta.total;
        state.list.page = action.payload.meta.page;
        state.list.limit = action.payload.meta.limit;
        state.list.totalPages = action.payload.meta.totalPages;
      })
      .addCase(getAllSurveySets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getSurveySetById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.selected = null;
      })
      .addCase(getSurveySetById.fulfilled, (state, action) => {
        state.loading = false;
        state.selected = action.payload;
      })
      .addCase(getSurveySetById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Error fetching survey set';
        state.selected = null;
      });

    builder
      .addCase(updateSurveySet.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSurveySet.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(updateSurveySet.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Error updating survey set';
      });

    builder
      .addCase(getSurveySetBySiteId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSurveySetBySiteId.fulfilled, (state, action) => {
        state.loading = false;
        state.surveysetsBySite.data = action.payload.data;
      })
      .addCase(getSurveySetBySiteId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Error updating survey set';
      });

    builder
      .addCase(getSurveySetUsersCount.pending, (state) => {
        state.loadingEstimatedCount = true;
        state.estimatedCountError = null;
      })
      .addCase(getSurveySetUsersCount.fulfilled, (state, action) => {
        state.loadingEstimatedCount = false;
        state.estimatedCount = action.payload.data?.count ?? 0;
      })
      .addCase(getSurveySetUsersCount.rejected, (state, action) => {
        state.loadingEstimatedCount = false;
        state.estimatedCountError = (action.payload as string) || 'Error fetching audience count';
        state.estimatedCount = null;
      });

    builder
      .addCase(getPosterCode.pending, (state) => {
        state.loadingPosterCode = true;
        state.posterCodeError = null;
      })
      .addCase(getPosterCode.fulfilled, (state, action) => {
        state.loadingPosterCode = false;
        state.posterCodeData = action.payload;
      })
      .addCase(getPosterCode.rejected, (state, action) => {
        state.loadingPosterCode = false;
        state.posterCodeError = (action.payload as string) || 'Error fetching poster code data';
        state.posterCodeData = null;
      });
  },
});

export const {
  clearSurveySetsError,
  clearSelectedSurveySet,
  updateSurveySetInList,
  removeSurveySetFromList,
} = surveySetSlice.actions;
export default surveySetSlice.reducer;

export const { resetPosterCodeState, setPage, setPageSize, setFilters } = surveySetSlice.actions;
